import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { useReactFlow } from '@xyflow/react';
import { useMemo, useRef, useState, useEffect } from 'react';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';

type EditableNodeTitleProps = {
  id: string;
  data?: Record<string, unknown>;
  defaultTitle: string;
  variant?: 'default' | 'modern';
  displayMaxChars?: number;
};

// Storage/editing max length (do NOT confuse with display truncation length).
const TITLE_MAX_LEN = 128;

const limitChars = (input: string, maxChars: number): string => {
  // Use Array.from to count unicode codepoints (good enough for most cases).
  const chars = Array.from(String(input ?? ''));
  if (chars.length <= maxChars) return chars.join('');
  return chars.slice(0, maxChars).join('');
};

const truncateWithDots = (input: string, maxChars: number): { text: string; truncated: boolean } => {
  const s = String(input ?? '');
  const chars = Array.from(s);
  if (chars.length <= maxChars) return { text: s, truncated: false };
  return { text: `${chars.slice(0, maxChars).join('')}...`, truncated: true };
};

export function EditableNodeTitle({
  id,
  data,
  defaultTitle,
  variant = 'default',
  displayMaxChars = 16,
}: EditableNodeTitleProps) {
  const { t } = useI18n();
  const { updateNode } = useReactFlow();
  const isPreview = usePreviewCanvas();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const cancelRef = useRef(false);

  const rawTitle = useMemo(() => {
    try {
      const custom = (data as any)?.name;
      if (typeof custom === 'string' && custom.trim()) return custom as string;
      return defaultTitle;
    } catch {
      return defaultTitle;
    }
  }, [data, defaultTitle]);

  const { text: displayTitle, truncated: isTruncated } = useMemo(() => {
    return truncateWithDots(rawTitle, displayMaxChars);
  }, [rawTitle, displayMaxChars]);

  const startEdit = () => {
    if (isPreview) return;
    try {
      const custom = (data as any)?.name as string | undefined;
      setValue((custom ?? defaultTitle) || '');
    } catch {
      setValue(defaultTitle);
    }
    cancelRef.current = false;
    setIsEditing(true);
  };

  const commit = () => {
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    const next = (value ?? '').trim();
    setIsEditing(false);
    if (!next) return;
    const limited = limitChars(next, TITLE_MAX_LEN);
    try {
      // Avoid unnecessary updates
      if (limited === rawTitle) return;
      updateNode(id, { data: { ...(data as any), name: limited } });
    } catch {}
  };

  useEffect(() => {
    if (isPreview && isEditing) {
      setIsEditing(false);
    }
  }, [isPreview, isEditing]);

  return isEditing && !isPreview ? (
    <Input
      value={value}
      autoFocus
      onChange={(e) => setValue(limitChars(e.target.value, TITLE_MAX_LEN))}
      onBlur={commit}
      onPointerDown={(e) => {
        // Prevent ReactFlow node drag when selecting text inside the input
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          cancelRef.current = true;
          setIsEditing(false);
        }
      }}
      maxLength={TITLE_MAX_LEN}
      // Make it look like inline text (no box) and keep typography stable
      className={cn(
        "nodrag h-auto bg-transparent border-0 shadow-none px-0 py-0 rounded-none tracking-tight focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
        // NOTE: `Input` defaults to `md:text-sm`; override to keep size stable when editing.
        variant === 'modern' ? "w-64 !text-base md:!text-base font-bold text-foreground/90" : "w-56 font-mono !text-base md:!text-lg font-semibold"
      )}
    />
  ) : (
    <p
      className={cn(
        "nodrag tracking-tight placeholder-flow",
        variant === 'modern' ? "text-base md:text-base font-bold text-foreground/90" : "font-mono text-base md:text-lg font-semibold"
      )}
      title={isPreview ? undefined : (isTruncated ? rawTitle : t('Double-click to rename'))}
      onPointerDown={(e) => {
        // Allow text selection without dragging the node
        e.stopPropagation();
      }}
      onDoubleClick={(e) => {
        if (isPreview) return;
        e.stopPropagation();
        startEdit();
      }}
    >
      {displayTitle}
    </p>
  );
}

export default EditableNodeTitle;


